import { useState } from "react";
import { api } from "../../lib/api";
import { toCSV, downloadCSV, formatDateBR } from "../../lib/csv";
import { FileDown, Database, Package, Users, Contact, ShoppingBag, Lock, Boxes, Settings2 } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

const CARDS = [
  {
    key: "vendas",
    label: "Vendas",
    description: "Todas as vendas registradas (uma linha por item vendido).",
    icon: ShoppingBag,
    color: "bg-neutral-950 text-white",
    load: () => api.get("/vendas"),
    toRows: (vendas, ctx) => {
      const rows = [];
      vendas.forEach((v) => {
        const unidade = ctx.unidadesMap[v.unidade_id] || "";
        if (!v.itens || v.itens.length === 0) {
          rows.push({ ...v, unidade, item: null });
        } else {
          v.itens.forEach((it) => rows.push({ ...v, unidade, item: it }));
        }
      });
      return rows;
    },
    headers: [
      { label: "ID Venda", get: (r) => r.id },
      { label: "Data", get: (r) => formatDateBR(r.data_venda) },
      { label: "Unidade", get: (r) => r.unidade },
      { label: "Vendedor", get: (r) => r.vendedor_nome },
      { label: "Cliente", get: (r) => r.cliente_nome || "" },
      { label: "Placa", get: (r) => r.placa || "" },
      { label: "KM", get: (r) => r.km_atual ?? "" },
      { label: "Produto", get: (r) => r.item?.nome || "" },
      { label: "Qtd", get: (r) => r.item?.quantidade || "" },
      { label: "Preco Unit", get: (r) => r.item ? r.item.preco_unitario.toFixed(2).replace(".", ",") : "" },
      { label: "Subtotal Item", get: (r) => r.item ? r.item.subtotal.toFixed(2).replace(".", ",") : "" },
      { label: "Total Bruto", get: (r) => r.total_bruto.toFixed(2).replace(".", ",") },
      { label: "% Desconto", get: (r) => r.percentual_desconto.toFixed(2).replace(".", ",") },
      { label: "Desconto R$", get: (r) => r.total_desconto.toFixed(2).replace(".", ",") },
      { label: "Total Liquido", get: (r) => r.total_liquido.toFixed(2).replace(".", ",") },
      { label: "CMV", get: (r) => r.total_custo_cmv.toFixed(2).replace(".", ",") },
      { label: "Lucro", get: (r) => r.lucro.toFixed(2).replace(".", ",") },
    ],
  },
  {
    key: "clientes",
    label: "Clientes",
    description: "Base completa de clientes com placa, veículo e KM.",
    icon: Contact,
    color: "bg-blue-600 text-white",
    load: () => api.get("/clientes"),
    toRows: (arr, ctx) => arr.map((c) => ({ ...c, unidade: ctx.unidadesMap[c.unidade_id] || "" })),
    headers: [
      { label: "ID", get: (r) => r.id },
      { label: "Nome", get: (r) => r.nome },
      { label: "Celular", get: (r) => r.celular || "" },
      { label: "Telefone Fixo", get: (r) => r.telefone || "" },
      { label: "CPF", get: (r) => r.cpf || "" },
      { label: "Placa", get: (r) => r.placa },
      { label: "Modelo Veiculo", get: (r) => r.modelo_veiculo },
      { label: "KM Ultima Troca", get: (r) => r.km_ultima_troca },
      { label: "Data Ultima Troca", get: (r) => r.data_ultima_troca ? formatDateBR(r.data_ultima_troca) : "" },
      { label: "Unidade", get: (r) => r.unidade },
      { label: "Criado em", get: (r) => formatDateBR(r.created_at) },
    ],
  },
  {
    key: "estoque",
    label: "Estoque",
    description: "Estoque de todas as unidades com custo e preço de venda.",
    icon: Boxes,
    color: "bg-red-600 text-white",
    load: () => api.get("/estoque"),
    toRows: (arr, ctx) => arr.map((e) => ({ ...e, unidade: ctx.unidadesMap[e.unidade_id] || "" })),
    headers: [
      { label: "ID Estoque", get: (r) => r.id },
      { label: "Produto ID", get: (r) => r.produto_id },
      { label: "Produto", get: (r) => r.produto_nome },
      { label: "Categoria", get: (r) => r.categoria },
      { label: "Unidade", get: (r) => r.unidade },
      { label: "Qtd", get: (r) => r.quantidade_atual },
      { label: "Preco Custo", get: (r) => Number(r.preco_custo_global || 0).toFixed(2).replace(".", ",") },
      { label: "Preco Venda", get: (r) => Number(r.preco_venda || 0).toFixed(2).replace(".", ",") },
      { label: "Valor Custo Total", get: (r) => ((r.preco_custo_global || 0) * r.quantidade_atual).toFixed(2).replace(".", ",") },
      { label: "Valor Venda Total", get: (r) => ((r.preco_venda || 0) * r.quantidade_atual).toFixed(2).replace(".", ",") },
    ],
  },
  {
    key: "produtos",
    label: "Produtos (Catálogo)",
    description: "Catálogo global de produtos e custos.",
    icon: Package,
    color: "bg-neutral-700 text-white",
    load: () => api.get("/produtos"),
    toRows: (arr) => arr,
    headers: [
      { label: "ID", get: (r) => r.id },
      { label: "Nome", get: (r) => r.nome },
      { label: "Categoria", get: (r) => r.categoria },
      { label: "Preco Custo Global", get: (r) => Number(r.preco_custo_global).toFixed(2).replace(".", ",") },
      { label: "Criado em", get: (r) => formatDateBR(r.created_at) },
    ],
  },
  {
    key: "vendedores",
    label: "Vendedores",
    description: "Equipe de vendedores por unidade.",
    icon: Users,
    color: "bg-yellow-400 text-neutral-950",
    load: () => api.get("/vendedores"),
    toRows: (arr, ctx) => arr.map((v) => ({ ...v, unidade: ctx.unidadesMap[v.unidade_id] || "" })),
    headers: [
      { label: "ID", get: (r) => r.id },
      { label: "Nome", get: (r) => r.nome },
      { label: "Unidade", get: (r) => r.unidade },
      { label: "Ativo", get: (r) => (r.ativo ? "Sim" : "Nao") },
      { label: "Criado em", get: (r) => formatDateBR(r.created_at) },
    ],
  },
  {
    key: "fechamentos",
    label: "Fechamentos de Caixa",
    description: "Histórico completo de fechamentos (sessões) por unidade.",
    icon: Lock,
    color: "bg-green-700 text-white",
    load: () => api.get("/fechamentos"),
    toRows: (arr, ctx) => arr.map((f) => ({ ...f, unidade: ctx.unidadesMap[f.unidade_id] || f.unidade_nome })),
    headers: [
      { label: "ID", get: (r) => r.id },
      { label: "Numero", get: (r) => r.numero },
      { label: "Unidade", get: (r) => r.unidade },
      { label: "Inicio Periodo", get: (r) => r.periodo_inicio ? formatDateBR(r.periodo_inicio) : "" },
      { label: "Fim Periodo", get: (r) => formatDateBR(r.fechado_em) },
      { label: "Qtd Vendas", get: (r) => r.qtd_vendas },
      { label: "Qtd Itens", get: (r) => r.qtd_itens },
      { label: "Total Bruto", get: (r) => r.total_bruto.toFixed(2).replace(".", ",") },
      { label: "Total Desconto", get: (r) => r.total_desconto.toFixed(2).replace(".", ",") },
      { label: "Total Liquido", get: (r) => r.total_liquido.toFixed(2).replace(".", ",") },
      { label: "CMV", get: (r) => r.total_custo_cmv.toFixed(2).replace(".", ",") },
      { label: "Lucro", get: (r) => r.lucro.toFixed(2).replace(".", ",") },
    ],
  },
  {
    key: "unidades",
    label: "Unidades",
    description: "Cadastro das unidades operadas.",
    icon: Settings2,
    color: "bg-neutral-600 text-white",
    load: () => api.get("/unidades"),
    toRows: (arr) => arr,
    headers: [
      { label: "ID", get: (r) => r.id },
      { label: "Nome", get: (r) => r.nome },
      { label: "Slug", get: (r) => r.slug },
      { label: "Cor Tema", get: (r) => r.cor_tema },
    ],
  },
];

export default function Backup() {
  const [loading, setLoading] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);

  const exportOne = async (card) => {
    setLoading(card.key);
    try {
      const [data, unidadesRes] = await Promise.all([
        card.load(),
        api.get("/unidades"),
      ]);
      const unidadesMap = unidadesRes.data.reduce(
        (acc, u) => ({ ...acc, [u.id]: u.nome }),
        {}
      );
      const rows = card.toRows(data.data, { unidadesMap });
      const csv = toCSV(rows, card.headers);
      downloadCSV(`${card.key}_${today()}.csv`, csv);
      toast.success(`${card.label}: ${rows.length} linhas exportadas`);
    } catch (e) {
      toast.error(`Erro ao exportar ${card.label}`);
    } finally {
      setLoading(null);
    }
  };

  const exportAll = async () => {
    setLoadingAll(true);
    try {
      const unidadesRes = await api.get("/unidades");
      const unidadesMap = unidadesRes.data.reduce(
        (acc, u) => ({ ...acc, [u.id]: u.nome }),
        {}
      );
      for (const card of CARDS) {
        const res = await card.load();
        const rows = card.toRows(res.data, { unidadesMap });
        const csv = toCSV(rows, card.headers);
        downloadCSV(`${card.key}_${today()}.csv`, csv);
        // pequeno delay para o browser não bloquear múltiplos downloads
        await new Promise((r) => setTimeout(r, 300));
      }
      toast.success("Backup completo exportado");
    } catch (e) {
      toast.error("Erro no backup completo");
    } finally {
      setLoadingAll(false);
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Backup & Exportação
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Exportar Dados
          </h2>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Baixe todas as suas informações em formato CSV (separador{" "}
            <code className="font-mono bg-neutral-100 px-1">;</code> e BOM UTF-8, pronto
            para abrir no Excel em PT-BR). Disponível a qualquer momento para backup ou
            análise externa.
          </p>
        </div>
        <button
          data-testid="backup-export-all"
          onClick={exportAll}
          disabled={loadingAll}
          className="bg-yellow-400 hover:bg-yellow-500 text-neutral-950 h-12 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Database className="w-4 h-4" />
          {loadingAll ? "Exportando..." : "Backup Completo"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((card) => {
          const Icon = card.icon;
          const active = loading === card.key;
          return (
            <div
              key={card.key}
              className="bg-white border-2 border-neutral-900 p-5 flex flex-col"
            >
              <div
                className={`w-12 h-12 ${card.color} flex items-center justify-center mb-4`}
              >
                <Icon className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <h3 className="font-display text-xl font-black tracking-tight">
                {card.label}
              </h3>
              <p className="text-sm text-neutral-600 mt-1 flex-1">{card.description}</p>
              <button
                data-testid={`backup-${card.key}`}
                onClick={() => exportOne(card)}
                disabled={active}
                className="mt-4 bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 cta-brutal disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FileDown className="w-4 h-4" />
                {active ? "Exportando..." : "Exportar CSV"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-neutral-100 border-2 border-neutral-900 p-5">
        <div className="flex items-start gap-3">
          <Database className="w-6 h-6 mt-0.5 text-neutral-700" />
          <div>
            <h4 className="font-display font-black text-lg">
              Coleções ativas no banco
            </h4>
            <p className="text-sm text-neutral-700 mt-1">
              Todas as informações do sistema são persistidas em 8 coleções:{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                unidades
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                configuracoes
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                vendedores
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                produtos
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                estoque_unidade
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                clientes
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                vendas
              </code>{" "}
              ·{" "}
              <code className="font-mono text-xs bg-white px-1 py-0.5 border border-neutral-300">
                fechamentos
              </code>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
